// Third-party
import React, { CSSProperties } from 'react';
import classNames from 'classnames';

// Utils
import { FormattedMessage, formatMessageWithLocale, formatLocalizedString, formatDateWithLocale, formatMessage, LOCALE } from '../../../../utils/i18n';
import { PROPOSAL_PATTERN, PROPOSAL_ORIENTATION } from '../../utils/constants';
import domUtils from '../../../../utils/dom';
import colorUtils from '../../../../utils/colors';
import Icon from '@/views/components/common/Icon';

// Images
import { ReactComponent as ZigZagPattern } from '../../../../images/patterns/zigzag.svg?react';
import { ReactComponent as LinesPattern } from '../../../../images/patterns/lines.svg?react';
import { ReactComponent as RadialPattern } from '../../../../images/patterns/radial.svg?react';
import { ReactComponent as EqualizerPattern } from '../../../../images/patterns/equalizer.svg?react';

// Namespaces
import * as ProposalNS from '../../Proposal';

import Dictionary from '@/types/dictionary';
import Nullable from '@/types/nullable';
import LocalizedString from '@/types/localized-string';
import Locale from '@/types/locale';
import { format } from 'path';

enum Base64Images {
  ProfileImgSrc = 'profileImgSrc',
  LogoSrc = 'logoSrc',
}

export type ProposalHeaderInfos = {
  fname?: string;
  lname?: string;
  email?: string;
  title?: string;
  company?: string;
  phone?: string;
  address1?: string;
  address2?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
  clientName?: string;
  proposalDate?: Date;
  profileImgUrl?: string;
  logoUrl?: string;
  websiteUrl?: string;
  comparisonName?: string;
};

type Props = {
  locale?: Locale;
  headerInfos: ProposalHeaderInfos;
  hideGradient?: boolean;
  styleOptions: ProposalNS.ProposalStyleOptionsV2;
  bannerStyles?: ProposalNS.ProposalStyleBanners;
  customDisclaimer?: LocalizedString;
};

type State = {
  base64Images: Dictionary<string>;
};

const BACKGROUND_PATTERN: Dictionary<Nullable<React.FunctionComponent<React.SVGProps<SVGSVGElement>>>> = {
  [PROPOSAL_PATTERN.ZIGZAG]: ZigZagPattern,
  [PROPOSAL_PATTERN.LINES]: LinesPattern,
  [PROPOSAL_PATTERN.RADIAL]: RadialPattern,
  [PROPOSAL_PATTERN.EQUALIZER]: EqualizerPattern,
  [PROPOSAL_PATTERN.NONE]: null,
};

class ProposalHeaderComponent extends React.Component<Props, State> {
  static defaultProps = {
    headerInfos: {},
  };

  readonly state: State = {
    base64Images: {
      [Base64Images.ProfileImgSrc]: '',
      [Base64Images.LogoSrc]: '',
    },
  };

  componentDidMount() {
    this.convertHeaderImages();
  }

  componentDidUpdate(prevProps: Props) {
    const {
      headerInfos: { profileImgUrl, logoUrl },
    } = this.props;
    const {
      headerInfos: { profileImgUrl: prevProfileImgUrl, logoUrl: prevLogoUrl },
    } = prevProps;

    if (profileImgUrl !== prevProfileImgUrl) {
      this.convertUrlToBase64(Base64Images.ProfileImgSrc, profileImgUrl);
    }

    if (logoUrl !== prevLogoUrl) {
      this.convertUrlToBase64(Base64Images.LogoSrc, logoUrl);
    }
  }

  convertHeaderImages = () => {
    const {
      headerInfos: { logoUrl, profileImgUrl },
    } = this.props;

    this.convertUrlToBase64(Base64Images.LogoSrc, logoUrl);
    this.convertUrlToBase64(Base64Images.ProfileImgSrc, profileImgUrl);
  };

  convertUrlToBase64 = (img: Base64Images, url?: string) => {
    if (!url) {
      return this.setState(prevState => ({
        base64Images: {
          ...prevState.base64Images,
          [img]: '',
        },
      }));
    }

    // If image is an SVG, safe to use as-is.
    if (url.match(/^.*\.(svg)$/)) {
      return this.setState(prevState => ({
        base64Images: {
          ...prevState.base64Images,
          [img]: url,
        },
      }));
    }

    return domUtils.encodeImage(url).then(base64 => {
      this.setState(prevState => ({
        base64Images: {
          ...prevState.base64Images,
          [img]: `data:image/jpeg;base64,${base64}`,
        },
      }));
    });
  };

  render() {
    const {
      headerInfos,
      locale,
      hideGradient,
      styleOptions: { orientation },
      bannerStyles,
      customDisclaimer,
    } = this.props;

    let style: CSSProperties = {};
    let BackgroundPattern;
    let patternColor;

    if (bannerStyles) {
      const { background, border, color, pattern, gradientRatio } = bannerStyles;
      patternColor = bannerStyles.patternColor;

      // TODO -- CON-1286: Set pattern color based on background color's luminance
      BackgroundPattern = BACKGROUND_PATTERN[pattern || PROPOSAL_PATTERN.ZIGZAG];

      style = {
        background: !hideGradient ? colorUtils.getCssGradient(background, gradientRatio) : background,
        borderBottom: border && `1px solid ${border}`,
        color,
      };
    }

    const {
      base64Images: { profileImgSrc },
    } = this.state;
    const isLandscapeOrientation = orientation && orientation === PROPOSAL_ORIENTATION.LANDSCAPE;

    return (
      <div data-private data-dd-privacy="mask" className={classNames('proposal-header-component', { landscape: isLandscapeOrientation })} style={style}>
        {headerInfos.profileImgUrl && (
          <div className="profile-picture">
            <img alt="" src={profileImgSrc} />
          </div>
        )}

        <div className="main-infos">
          <div className="name-infos">
            <div className="field name">
              <span>{`${headerInfos.fname || '\xa0'} ${headerInfos.lname || '\xa0'}`}</span>
            </div>
            {headerInfos.title && (
              <div className="field title">
                <span>{headerInfos.title}</span>
              </div>
            )}

            <div className="field company">
              <span>{headerInfos.company || '\xa0'}</span>
            </div>
          </div>

          <div className="contact-infos">
            <div className="field phone">
              <span>
                {headerInfos.phone && <Icon type="phone" color="rgba(255,255,255,0.8)" title={formatMessage('Proposal.AdvisorInfoForm.Phone')} />}

                {headerInfos.phone || '\xa0'}
              </span>
            </div>

            <div className="field email">
              <span>
                {headerInfos.email && <Icon type="drawer" color="rgba(255,255,255,0.8)" title={formatMessage('Proposal.AdvisorInfoForm.Email')} />}

                {headerInfos.email || '\xa0'}
              </span>
            </div>
          </div>
        </div>

        <div className="full-address">
          <div className="address-section-one">
            <div className="field address">
              <span>{[headerInfos.address1, headerInfos.address2].filter(v => v).join(', ') || '\xa0'}</span>
            </div>

            <div className="field province">
              <span>{[headerInfos.city, headerInfos.province, headerInfos.postalCode].filter(v => v).join(', ')}</span>
            </div>
          </div>

          <div className="address-section-two">
            <div className="field country">
              <span>{headerInfos.country || '\xa0'}</span>
            </div>

            <div className="field website">
              <span>{headerInfos.websiteUrl || '\xa0'}</span>
            </div>
          </div>
        </div>

        {headerInfos.clientName && (
          <div className="proposal-contact" data-private data-dd-privacy="mask">
            <span>
              <FormattedMessage id="Proposal.PreparedFor" locale={locale} />
              {'\xa0'}
              <strong>{headerInfos.clientName}</strong>

              {headerInfos.proposalDate && (
                <>
                  {'\xa0'}
                  <FormattedMessage id="Common.On" locale={locale} />
                  {'\xa0'}
                  <strong>
                    {formatDateWithLocale(headerInfos.proposalDate, locale || LOCALE.EN_CA, formatMessageWithLocale('Common.DateFormats.Long', locale))}
                  </strong>
                </>
              )}
            </span>
          </div>
        )}

        {headerInfos.logoUrl && (
          <div className="proposal-logo-picture">
            <img alt="" src={headerInfos.logoUrl} />
          </div>
        )}

        {headerInfos.comparisonName && <div className="proposal-comparison-name">{headerInfos.comparisonName}</div>}

        {BackgroundPattern != null && <BackgroundPattern stroke={patternColor} className="proposal-pattern" />}

        {customDisclaimer !== undefined && (
          <div className="disclaimer" data-private data-dd-privacy="mask">
            <p>
              <span>{formatLocalizedString(customDisclaimer, locale)}</span>
            </p>
          </div>
        )}
      </div>
    );
  }
}

export default ProposalHeaderComponent;
